<template>
    <div
        class="flex flex-col"
        :class="[
            dens === 'compact' ? 'gap-3' : 'gap-5',
            openCheckpoint || decidedRecently ? 'pb-28' : '',
        ]"
    >
        <!-- Seletor de período + densidade + dossiê: renderizados na toolbar
             única do UserAudit. Sem o alvo, rendem inline no topo. -->
        <Teleport
            v-if="mounted"
            to="#audit-toolbar"
            :disabled="!toolbarTargetExists"
        >
            <div class="flex min-w-0 flex-wrap items-center gap-3">
                <div
                    role="group"
                    aria-label="Selecionar período de análise"
                    v-tooltip="'Janela de análise: ' + periodRangeLabel"
                    class="inline-flex items-center gap-0.5 rounded-full bg-gray-100 p-0.5 dark:bg-gray-800"
                >
                    <button
                        v-for="option in periodOptions"
                        :key="option"
                        type="button"
                        :aria-pressed="period === option"
                        @click="period = option"
                        :class="[
                            'rounded-full px-2.5 py-1 text-xs font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
                            period === option
                                ? 'bg-indigo-600 text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white',
                        ]"
                    >
                        {{ option === 0 ? "Total" : option + "d" }}
                    </button>
                </div>
                <span
                    class="hidden text-xs text-gray-400 dark:text-gray-500 lg:inline"
                >
                    {{ periodRangeLabel }}
                </span>

                <!-- Densidade: Respirada/Compacta (paddings e gaps da tela) -->
                <div
                    role="group"
                    aria-label="Densidade da tela"
                    class="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800"
                >
                    <button
                        v-for="option in densOptions"
                        :key="option.value"
                        type="button"
                        :aria-pressed="dens === option.value"
                        @click="setDens(option.value)"
                        :class="[
                            'rounded-md px-2.5 py-1 text-xs transition-all duration-150 focus:outline-none',
                            dens === option.value
                                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-600 dark:text-white'
                                : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300',
                        ]"
                    >
                        {{ option.label }}
                    </button>
                </div>

                <button
                    @click="generateDossier"
                    :disabled="generatingDossier"
                    v-tooltip="'Gerar dossiê do usuário'"
                    class="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white transition duration-150 ease-in-out hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <svg
                        v-if="!generatingDossier"
                        class="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        ></path>
                    </svg>
                    <svg
                        v-else
                        class="h-4 w-4 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    {{ generatingDossier ? "Gerando..." : "Dossiê" }}
                </button>
            </div>
        </Teleport>

        <!-- Coluna principal + painel do agente (empilha em telas estreitas) -->
        <div
            class="flex flex-wrap items-start"
            :class="dens === 'compact' ? 'gap-3' : 'gap-5'"
        >
            <div
                class="flex min-w-0 flex-[10_1_420px] flex-col"
                :class="dens === 'compact' ? 'gap-3' : 'gap-5'"
            >
                <!-- Banner de caso aberto: o fato crítico direto -->
                <risk-case-banner
                    v-if="openCheckpoint"
                    :headline="caseHeadline"
                    :meta="caseMeta"
                />

                <!-- Julgamentos: as três perguntas, clicáveis para a evidência -->
                <div
                    ref="julg"
                    class="rounded-xl transition-shadow duration-500"
                    :class="flashClass('julg')"
                >
                    <seller-farol
                        :signals="farolSignals"
                        :loading="portraitLoading"
                        @go="goNav"
                    />
                </div>

                <!-- Fichas de retrato -->
                <seller-portrait-grid
                    :portrait="portrait"
                    :loading="portraitLoading"
                    :error="portraitError"
                    :keys="[
                        'coverage',
                        'exposure',
                        'reserve',
                        'cashouts',
                        'card_share',
                    ]"
                    @open-detail="openSnapshotDetail"
                />

                <!-- Seção: Reserva & Runoff -->
                <div
                    ref="runoff"
                    class="overflow-hidden rounded-lg border border-gray-200 transition-shadow duration-500 dark:border-gray-700"
                    :class="flashClass('runoff')"
                >
                    <collapsible-info-card
                        title="Reserva &amp; Runoff"
                        icon="ScaleIcon"
                        :collapsed="!sections.runoff"
                        :priority="runoffPriority"
                        :summary="runoffSummary"
                        :badge="runoffBadge"
                        :loading="runoffSummary === ''"
                        @update:collapsed="sections.runoff = !$event"
                    >
                        <reserve-runoff-card
                            :key="runoffKey"
                            :user="user"
                            @data-loaded="updateRunoffSummary"
                        />
                    </collapsible-info-card>
                </div>

                <!-- Seção: Conduta & Score -->
                <div
                    ref="conduta"
                    class="overflow-hidden rounded-lg border border-gray-200 transition-shadow duration-500 dark:border-gray-700"
                    :class="flashClass('conduta')"
                >
                    <collapsible-info-card
                        title="Conduta &amp; Score"
                        icon="ExclamationCircleIcon"
                        :collapsed="!sections.conduta"
                        :priority="condutaPriority"
                        :summary="condutaSummary"
                        :badge="condutaBadge"
                        @update:collapsed="sections.conduta = !$event"
                    >
                        <div class="flex flex-col gap-3">
                            <seller-portrait-grid
                                :portrait="portrait"
                                :loading="portraitLoading"
                                :error="portraitError"
                                :keys="['disputes', 'box_disputes', 'refunds']"
                                @open-detail="openSnapshotDetail"
                            />

                            <div
                                ref="score"
                                class="rounded-lg transition-shadow duration-500"
                                :class="flashClass('score')"
                            >
                                <score
                                    :user="user"
                                    @data-loaded="updateScoreSummary"
                                />
                            </div>

                            <div
                                class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
                            >
                                <collapsible-info-card
                                    title="Análise do Sistema"
                                    icon="ExclamationCircleIcon"
                                    :collapsed="!userAnalysis"
                                    :priority="systemAnalysisPriority"
                                    :summary="systemAnalysisSummary"
                                    :badge="systemAnalysisBadge"
                                    :loading="!userAnalysis"
                                >
                                    <user-system-analysis
                                        :user="user"
                                        @data-loaded="
                                            updateSystemAnalysisSummary
                                        "
                                    />
                                </collapsible-info-card>

                                <collapsible-info-card
                                    title="BINs de Cartão Mais Usados"
                                    icon="CreditCardIcon"
                                    :collapsed="true"
                                    :summary="binSummary"
                                    :priority="binPriority"
                                    :badge="binBadge"
                                    :period-label="periodLabel"
                                    :loading="binSummary === ''"
                                >
                                    <user-bin-usage-card
                                        :user="user"
                                        :period="period"
                                        @data-loaded="updateBinSummary"
                                    />
                                </collapsible-info-card>
                            </div>
                        </div>
                    </collapsible-info-card>
                </div>

                <!-- Seção: Operação & Vendas -->
                <div
                    ref="operacao"
                    class="overflow-hidden rounded-lg border border-gray-200 transition-shadow duration-500 dark:border-gray-700"
                    :class="flashClass('operacao')"
                >
                    <collapsible-info-card
                        title="Operação &amp; Vendas"
                        icon="ChartBarIcon"
                        :collapsed="!sections.operacao"
                        :priority="operacaoPriority"
                        :summary="operacaoSummary"
                        :period-label="periodLabel"
                        @update:collapsed="sections.operacao = !$event"
                    >
                        <div class="flex flex-col gap-4">
                            <seller-portrait-grid
                                :portrait="portrait"
                                :loading="portraitLoading"
                                :error="portraitError"
                                :keys="['tpv', 'sales_count', 'days_with_sale']"
                                @open-detail="openSnapshotDetail"
                            />

                            <commission-cards
                                :user="user"
                                :period="period"
                            ></commission-cards>

                            <commissions-chart
                                :user="user"
                                :filters="chartFilters"
                                :period="period"
                            />

                            <div
                                class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
                            >
                                <collapsible-info-card
                                    title="Métodos de Pagamento"
                                    icon="BanknotesIcon"
                                    :collapsed="true"
                                    :summary="paymentMethodSummary"
                                    :priority="paymentMethodPriority"
                                    :badge="paymentMethodBadge"
                                    :period-label="periodLabel"
                                    :loading="paymentMethodSummary === ''"
                                >
                                    <user-payment-method-card
                                        :user="user"
                                        :period="period"
                                        @data-loaded="
                                            updatePaymentMethodSummary
                                        "
                                    />
                                </collapsible-info-card>

                                <collapsible-info-card
                                    title="Tipos de Comissão"
                                    icon="ChartBarIcon"
                                    :collapsed="true"
                                    :summary="commissionTypesSummary"
                                    :priority="commissionTypesPriority"
                                    :badge="commissionTypesBadge"
                                    :loading="commissionTypesSummary === ''"
                                >
                                    <user-commission-types-card
                                        :user="user"
                                        @data-loaded="
                                            updateCommissionTypesSummary
                                        "
                                    />
                                </collapsible-info-card>
                            </div>
                        </div>
                    </collapsible-info-card>
                </div>

                <!-- Seção: Histórico de decisões -->
                <div
                    ref="hist"
                    class="overflow-hidden rounded-lg border border-gray-200 transition-shadow duration-500 dark:border-gray-700"
                    :class="flashClass('hist')"
                >
                    <collapsible-info-card
                        title="Histórico de decisões"
                        icon="StarIcon"
                        :collapsed="!sections.hist"
                        :summary="historySummary"
                        @update:collapsed="sections.hist = !$event"
                    >
                        <div
                            v-if="decidedCheckpoints.length === 0"
                            class="text-sm text-gray-500 dark:text-gray-400"
                        >
                            Nenhuma decisão de reserva registrada para este
                            seller.
                        </div>
                        <div v-else class="flex flex-col gap-4">
                            <div
                                v-for="checkpoint in decidedCheckpoints"
                                :key="checkpoint.id"
                                class="flex gap-3"
                            >
                                <span
                                    class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                                    :class="
                                        checkpoint.status === 'decided'
                                            ? 'bg-indigo-500'
                                            : 'bg-gray-300 dark:bg-gray-600'
                                    "
                                ></span>
                                <div class="flex flex-col gap-0.5">
                                    <span
                                        class="text-[13px] font-semibold text-gray-900 dark:text-white"
                                    >
                                        {{ historyTitle(checkpoint) }}
                                    </span>
                                    <span
                                        class="text-xs text-gray-400 dark:text-gray-500"
                                    >
                                        {{ historyMeta(checkpoint) }}
                                    </span>
                                    <span
                                        v-if="checkpoint.decision_note"
                                        class="text-xs text-gray-500 dark:text-gray-400"
                                    >
                                        “{{ checkpoint.decision_note }}”
                                    </span>
                                </div>
                            </div>
                        </div>
                    </collapsible-info-card>
                </div>

                <!-- Detalhamento completo (fichas, escalas, evidências) -->
                <section
                    ref="snapshotDetail"
                    class="flex scroll-mt-4 flex-col gap-3"
                >
                    <button
                        type="button"
                        class="flex items-center gap-2 self-start text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500 transition-colors hover:text-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:text-gray-400 dark:hover:text-indigo-400"
                        :aria-expanded="showSnapshotDetail"
                        @click="showSnapshotDetail = !showSnapshotDetail"
                    >
                        <svg
                            class="h-3.5 w-3.5 transition-transform"
                            :class="{ 'rotate-90': showSnapshotDetail }"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 5l7 7-7 7"
                            ></path>
                        </svg>
                        Detalhamento do retrato (fichas, réguas e evidências)
                    </button>
                    <seller-snapshot-panel
                        v-if="showSnapshotDetail"
                        :user="user"
                    />
                </section>
            </div>

            <!-- Painel do agente: sticky, sempre visível -->
            <div
                ref="agent"
                class="sticky top-[68px] flex max-h-[calc(100vh-200px)] min-w-0 flex-[1_1_300px] flex-col rounded-xl transition-shadow duration-500"
                :class="flashClass('agent')"
            >
                <seller-ai-audit
                    :user="user"
                    :panel="true"
                    class="h-full"
                    @navigate="goNav"
                    @tour="startTour"
                />
            </div>
        </div>

        <!-- Barra de decisão: fixa no rodapé enquanto o caso está aberto -->
        <risk-decision-bar
            :checkpoint="openCheckpoint"
            :extend-options="extendOptions"
            :flash="flash === 'bar'"
            @decided="onDecided"
            @dismiss="decidedRecently = false"
        />

        <metrics-tour ref="tour" @highlight="onTourHighlight" />
    </div>
</template>

<script>
import CommissionCards from "../../../components/user/CommissionCards.vue";
import Score from "../../../components/user/Score.vue";
import SellerFarol from "../../../components/user/metrics/SellerFarol.vue";
import SellerSnapshotPanel from "../../../components/user/metrics/SellerSnapshotPanel.vue";
import SellerPortraitGrid from "../../../components/user/metrics/SellerPortraitGrid.vue";
import SellerAiAudit from "../../../components/user/metrics/SellerAiAudit.vue";
import RiskCaseBanner from "../../../components/user/metrics/RiskCaseBanner.vue";
import RiskDecisionBar from "../../../components/user/metrics/RiskDecisionBar.vue";
import MetricsTour from "../../../components/user/metrics/MetricsTour.vue";
import HelperMixin from "../../../mixins/HelperMixin.vue";
import CommissionsChart from "../../users/commissions/CommissionsChart.vue";
import UserBinUsageCard from "../../../components/user/UserBinUsageCard.vue";
import {
    ExclamationCircleIcon,
    CreditCardIcon,
    BanknotesIcon,
    ChartBarIcon,
    ScaleIcon,
    StarIcon,
} from "@heroicons/vue/24/outline";
import ReserveRunoffCard from "../../../components/user/metrics/ReserveRunoffCard.vue";
import UserSystemAnalysis from "../../../components/user/UserSystemAnalysis.vue";
import UserPaymentMethodCard from "../../../components/user/UserPaymentMethodCard.vue";
import UserCommissionTypesCard from "../../../components/user/UserCommissionTypesCard.vue";
import CollapsibleInfoCard from "../../../components/user/CollapsibleInfoCard.vue";

const STATUS_RANK = { critical: 2, attention: 1 };

const TRIGGER_LABELS = {
    parcela_grande: "Parcela grande",
    extensao_manual: "Extensão manual",
    churn_detectado: "Churn",
    cauda_final: "Cauda final",
    venda_sem_retencao: "Venda sem retenção",
    manual: "Manual",
};

const DECISION_LABELS = {
    liberar: "Liberação aprovada",
    estender: "Reserva estendida",
    travar_saque: "Saque travado",
};

export default {
    components: {
        CommissionsChart,
        SellerFarol,
        SellerSnapshotPanel,
        SellerPortraitGrid,
        SellerAiAudit,
        RiskCaseBanner,
        RiskDecisionBar,
        MetricsTour,
        Score,
        CommissionCards,
        UserBinUsageCard,
        ExclamationCircleIcon,
        CreditCardIcon,
        BanknotesIcon,
        ChartBarIcon,
        ScaleIcon,
        StarIcon,
        ReserveRunoffCard,
        UserSystemAnalysis,
        UserPaymentMethodCard,
        UserCommissionTypesCard,
        CollapsibleInfoCard,
    },
    props: ["user"],
    data() {
        return {
            filters: {
                text: "",
                status: [],
                payment_method: [],
            },
            // Janela de análise aplicada a todas as métricas temporais da tela
            period: 30,
            // 0 = Total (sem janela, todo o histórico)
            periodOptions: [7, 15, 30, 90, 0],
            // Densidade da tela (persistida): Respirada ou Compacta
            dens: "roomy",
            densOptions: [
                { value: "roomy", label: "Respirada" },
                { value: "compact", label: "Compacta" },
            ],
            // Retrato: buscado uma vez aqui e distribuído para os julgamentos
            // e as fichas.
            portrait: null,
            portraitLoading: true,
            portraitError: false,
            portraitSeq: 0,
            // Caso de risco: checkpoints de reserva do seller (pendente =
            // caso aberto → banner + barra de decisão)
            checkpoints: [],
            checkpointsSeq: 0,
            extendOptions: [15, 30, 45, 60],
            decidedRecently: false,
            runoffKey: 0,
            // Abertura das seções. Regra de estado inicial: tudo colapsado;
            // com caso aberto, expande a seção que motivou o caso (reserva).
            sections: {
                runoff: false,
                conduta: false,
                operacao: false,
                hist: false,
            },
            sectionsInitialized: false,
            // Destaque temporário disparado por julgamentos, agente e tour
            flash: null,
            flashTimer: null,
            // Dados para os summaries dos cards
            userAnalysis: null,
            scoreSummary: "",
            scorePriority: "normal",
            scoreBadge: null,
            scoreAmount: null,
            binSummary: "",
            binPriority: "normal",
            binBadge: null,
            paymentMethodSummary: "",
            paymentMethodPriority: "normal",
            paymentMethodBadge: null,
            systemAnalysisSummary: "",
            systemAnalysisPriority: "normal",
            systemAnalysisBadge: null,
            commissionTypesSummary: "",
            commissionTypesPriority: "normal",
            commissionTypesBadge: null,
            runoffSummary: "",
            runoffPriority: "normal",
            runoffBadge: null,
            runoffHeadline: "",
            // Estado do botão de gerar dossiê
            generatingDossier: false,
            // Teleport da toolbar: só depois do mount, com fallback inline
            mounted: false,
            toolbarTargetExists: false,
            // Painel detalhado do retrato (fichas e evidências), colapsado
            showSnapshotDetail: false,
        };
    },

    mixins: [HelperMixin],

    computed: {
        chartFilters() {
            return {
                ...this.filters,
                interval: this.period,
            };
        },

        periodLabel() {
            return this.period === 0 ? "Total" : `Últimos ${this.period} dias`;
        },

        periodRangeLabel() {
            if (this.period === 0) {
                return "todo o histórico";
            }

            const start = new Date();
            start.setDate(start.getDate() - this.period);

            const format = (date) =>
                date.toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                });

            return `${format(start)} – ${format(new Date())}`;
        },

        /** Checkpoint pendente mais urgente: é o "caso aberto" da tela. */
        openCheckpoint() {
            return (
                this.checkpoints.find(
                    (checkpoint) => checkpoint.status === "pending",
                ) ?? null
            );
        },

        decidedCheckpoints() {
            return this.checkpoints.filter((checkpoint) =>
                ["decided", "auto_resolved", "expired"].includes(
                    checkpoint.status,
                ),
            );
        },

        caseHeadline() {
            return (
                this.runoffHeadline ||
                this.openCheckpoint?.recommendation?.reason ||
                "Há um checkpoint de reserva aguardando decisão."
            );
        },

        caseMeta() {
            const checkpoint = this.openCheckpoint;

            if (!checkpoint) {
                return "";
            }

            const parts = [
                TRIGGER_LABELS[checkpoint.trigger] ?? checkpoint.trigger,
            ];

            if (checkpoint.due_date) {
                parts.push(
                    `decidir até ${this.shortDate(checkpoint.due_date)}`,
                );
            }

            return parts.join(" · ");
        },

        historySummary() {
            const count = this.decidedCheckpoints.length;

            if (count === 0) {
                return "sem decisões registradas";
            }

            return `${count} ${count === 1 ? "decisão" : "decisões"}`;
        },

        condutaPriority() {
            const worst = this.worstCard([
                "disputes",
                "box_disputes",
                "refunds",
            ]);
            const rank = STATUS_RANK[worst?.status] ?? 0;
            const scoreRank =
                { danger: 2, warning: 1 }[this.scorePriority] ?? 0;

            const combined = Math.max(rank, scoreRank);

            return combined === 2
                ? "danger"
                : combined === 1
                  ? "warning"
                  : "normal";
        },

        condutaBadge() {
            return (
                { danger: "Alerta", warning: "Atenção" }[
                    this.condutaPriority
                ] ?? null
            );
        },

        condutaSummary() {
            const parts = [];
            const disputes = this.cardByKey("disputes");

            if (disputes) {
                parts.push(`disputas ${disputes.value}`);
            }

            if (this.scoreAmount !== null) {
                parts.push(`score ${this.scoreAmount}`);
            }

            return parts.join(" · ") || "disputas, score e sinais de fraude";
        },

        operacaoPriority() {
            const worst = this.worstCard(["days_with_sale", "sales_count"]);

            return worst?.status === "critical"
                ? "danger"
                : worst?.status === "attention"
                  ? "warning"
                  : "normal";
        },

        operacaoSummary() {
            const tpv = this.cardByKey("tpv");
            const days = this.cardByKey("days_with_sale");
            const parts = [];

            if (tpv) {
                parts.push(`vendas ${tpv.value}`);
            }

            if (days) {
                parts.push(`${days.value} dias com venda`);
            }

            return parts.join(" · ") || "vendas, comissões e métodos";
        },

        /**
         * Os três julgamentos, derivados dos status que já estão na tela
         * (cards do retrato + veredito do runoff). Sem request extra e sem
         * divergir do que as seções mostram.
         */
        farolSignals() {
            const caixa = this.worstCard(["coverage", "reserve", "cashouts"]);
            const operacao = this.worstCard(["days_with_sale"]);
            const conduta = this.worstCard([
                "disputes",
                "box_disputes",
                "refunds",
            ]);

            const signals = [
                {
                    key: "conduta",
                    domain: "Conduta",
                    question: "Tem sinal de fraude?",
                    section: "conduta",
                    cta: "ver conduta & score →",
                    ...this.signalOf(
                        conduta,
                        "disputas e reembolsos dentro do limiar",
                    ),
                },
                {
                    key: "caixa",
                    domain: "Caixa",
                    question: "Posso liberar dinheiro?",
                    section: "runoff",
                    cta: "ver reserva & runoff →",
                    ...this.signalOf(
                        caixa,
                        "cobertura e saques dentro do limiar",
                    ),
                },
                {
                    key: "operacao",
                    domain: "Operação",
                    question: "O negócio está vivo?",
                    section: "operacao",
                    cta: "ver operação & vendas →",
                    ...this.signalOf(operacao, "vendendo em ritmo normal"),
                },
            ];

            // O runoff pode ser pior que os cards de caixa (parcela descoberta)
            const runoffRank =
                { danger: 2, warning: 1 }[this.runoffPriority] ?? 0;

            const caixaSignal = signals.find(
                (signal) => signal.key === "caixa",
            );

            if (runoffRank > (STATUS_RANK[caixa?.status] ?? 0)) {
                caixaSignal.status =
                    runoffRank === 2 ? "critical" : "attention";
                caixaSignal.reason = this.runoffSummary || caixaSignal.reason;
            }

            // Operação sem pendência não navega (sem seção para "resolver")
            const operacaoSignal = signals.find(
                (signal) => signal.key === "operacao",
            );

            if (operacaoSignal.status === "ok") {
                operacaoSignal.cta = "sem pendência";
                operacaoSignal.section = "operacao";
            }

            return signals;
        },
    },

    watch: {
        period() {
            // Evita misturar valores de janelas diferentes: some o resumo até o novo chegar
            this.binSummary = "";
            this.paymentMethodSummary = "";
            this.fetchPortrait();
        },

        user() {
            // Troca de seller: zera o caso na hora. A barra de decisão não
            // pode ficar nem um frame com o checkpoint do seller anterior.
            this.checkpoints = [];
            this.decidedRecently = false;
            this.sectionsInitialized = false;
            this.fetchPortrait();
            this.fetchCheckpoints();
        },
    },

    mounted() {
        this.toolbarTargetExists = Boolean(
            document.getElementById("audit-toolbar"),
        );
        this.mounted = true;

        try {
            const dens = localStorage.getItem("ticto_metricas_dens");

            if (dens === "compact" || dens === "roomy") {
                this.dens = dens;
            }
        } catch (error) {
            // Sem localStorage: fica na densidade padrão.
        }

        this.fetchPortrait();
        this.fetchCheckpoints();
    },

    beforeUnmount() {
        clearTimeout(this.flashTimer);
    },

    methods: {
        fetchPortrait() {
            const seq = ++this.portraitSeq;

            this.portraitLoading = true;
            this.portraitError = false;

            axios
                .get(`/api/user/${this.userId()}/metrics/portrait`, {
                    params: { period: this.period },
                })
                .then(({ data }) => {
                    // O operador pode trocar de período antes da resposta
                    // anterior chegar: só publica a última pedida.
                    if (seq === this.portraitSeq) {
                        this.portrait = data;
                    }
                })
                .catch(() => {
                    if (seq === this.portraitSeq) {
                        this.portraitError = true;
                    }
                })
                .finally(() => {
                    if (seq === this.portraitSeq) {
                        this.portraitLoading = false;
                    }
                });
        },

        fetchCheckpoints() {
            // Mesma guarda de sequência do retrato: a barra de decisão posta
            // no checkpoint carregado aqui, e uma resposta atrasada do seller
            // anterior registraria a decisão no seller errado.
            const seq = ++this.checkpointsSeq;

            axios
                .get(
                    `/api/financial/users/${this.userId()}/reserve-checkpoints`,
                )
                .then(({ data }) => {
                    if (seq !== this.checkpointsSeq) {
                        return;
                    }

                    this.checkpoints = data.checkpoints ?? [];
                    this.extendOptions =
                        data.extend_days_options ?? this.extendOptions;
                    this.applyInitialSections();
                })
                .catch(() => {
                    if (seq === this.checkpointsSeq) {
                        this.checkpoints = [];
                    }
                });
        },

        /**
         * Regra de estado inicial das seções: sem caso aberto, tudo
         * colapsado; com caso aberto, expande só a seção que o motivou
         * (checkpoints de reserva → Reserva & Runoff).
         */
        applyInitialSections() {
            if (this.sectionsInitialized) {
                return;
            }

            this.sectionsInitialized = true;

            if (this.openCheckpoint) {
                this.sections.runoff = true;
            }
        },

        onDecided() {
            this.decidedRecently = true;
            this.fetchCheckpoints();
            // Remonta o card de runoff: estender muda a agenda e as demais
            // decisões mudam a lista de checkpoints exibida lá dentro.
            this.runoffKey += 1;
        },

        setDens(value) {
            this.dens = value;

            try {
                localStorage.setItem("ticto_metricas_dens", value);
            } catch (error) {
                // Sem persistência a escolha vale só para a sessão.
            }
        },

        startTour() {
            this.$refs.tour?.start();
        },

        onTourHighlight(key) {
            if (key === null) {
                this.clearFlash();
                return;
            }

            if (key === "runoff") {
                this.sections.runoff = true;
            }

            this.applyFlash(key, false);

            if (key === "julg") {
                window.scrollTo({ top: 0, behavior: "smooth" });
            } else if (key === "runoff") {
                this.scrollToRef("runoff");
            }
        },

        /**
         * Navegação "onde está X": expande a seção alvo, rola até ela e
         * aplica o flash de destaque. Usada pelos julgamentos, pelo agente
         * e pelo botão "Ver … →".
         */
        goNav(target) {
            const sectionByTarget = {
                runoff: "runoff",
                conduta: "conduta",
                score: "conduta",
                operacao: "operacao",
                hist: "hist",
            };

            const section = sectionByTarget[target];

            if (section) {
                this.sections[section] = true;
            }

            this.applyFlash(target);

            this.$nextTick(() => {
                setTimeout(() => {
                    this.scrollToRef(section ?? target);
                }, 80);
            });
        },

        scrollToRef(name) {
            const el = this.$refs[name];

            if (!el) {
                return;
            }

            const node = el.$el ?? el;

            window.scrollTo({
                top: node.getBoundingClientRect().top + window.scrollY - 74,
                behavior: "smooth",
            });
        },

        applyFlash(key, autoClear = true) {
            clearTimeout(this.flashTimer);
            this.flash = key;

            if (autoClear) {
                this.flashTimer = setTimeout(() => {
                    this.flash = null;
                }, 2800);
            }
        },

        clearFlash() {
            clearTimeout(this.flashTimer);
            this.flash = null;
        },

        flashClass(key) {
            return this.flash === key
                ? "ring-3 ring-indigo-500/50 shadow-[0_8px_28px_rgba(91,75,232,0.22)]"
                : "";
        },

        userId() {
            return this.$route?.params?.user_id ?? this.user.id;
        },

        cardByKey(key) {
            return (this.portrait?.cards || []).find(
                (card) => card.key === key,
            );
        },

        worstCard(keys) {
            const byKey = new Map(
                (this.portrait?.cards || []).map((card) => [card.key, card]),
            );

            let worst = null;

            for (const key of keys) {
                const card = byKey.get(key);

                if (
                    card &&
                    (!worst ||
                        (STATUS_RANK[card.status] ?? 0) >
                            (STATUS_RANK[worst.status] ?? 0))
                ) {
                    worst = card;
                }
            }

            return worst;
        },

        signalOf(card, okReason) {
            if (card && (STATUS_RANK[card.status] ?? 0) > 0) {
                return {
                    status: card.status,
                    reason: `${card.label}: ${card.value}`,
                };
            }

            return { status: "ok", reason: okReason };
        },

        shortDate(value) {
            if (!value) {
                return "";
            }

            const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);

            return isNaN(parsed)
                ? value
                : parsed.toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                  });
        },

        historyTitle(checkpoint) {
            if (checkpoint.status === "decided") {
                return `${DECISION_LABELS[checkpoint.decision] ?? checkpoint.decision} — ${TRIGGER_LABELS[checkpoint.trigger] ?? checkpoint.trigger}`;
            }

            if (checkpoint.status === "auto_resolved") {
                return `Critério passou — ${TRIGGER_LABELS[checkpoint.trigger] ?? checkpoint.trigger}`;
            }

            return `Checkpoint expirado — ${TRIGGER_LABELS[checkpoint.trigger] ?? checkpoint.trigger}`;
        },

        historyMeta(checkpoint) {
            const parts = [];

            if (checkpoint.decided_at) {
                parts.push(
                    new Date(checkpoint.decided_at).toLocaleDateString(
                        "pt-BR",
                        { day: "2-digit", month: "2-digit", year: "numeric" },
                    ),
                );
            } else if (checkpoint.due_date) {
                parts.push(`prazo ${this.shortDate(checkpoint.due_date)}`);
            }

            return parts.join(" · ");
        },

        openSnapshotDetail() {
            this.showSnapshotDetail = true;

            this.$nextTick(() => {
                this.$refs.snapshotDetail?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            });
        },

        updateScoreSummary(scoreData) {
            if (!scoreData) {
                this.scoreSummary = "Score não disponível";
                this.scorePriority = "normal";
                this.scoreBadge = null;
                this.scoreAmount = null;
                return;
            }

            const { amount, level, bypass, historical } = scoreData;

            this.scoreAmount = amount;

            // Determina a prioridade baseada no score (escala 15-100, bloqueio em 50)
            if (amount < 50) {
                this.scorePriority = "danger";
            } else if (amount < 75) {
                this.scorePriority = "warning";
            } else {
                this.scorePriority = "normal";
            }

            // Badge com informações especiais
            let badgeText = level.charAt(0).toUpperCase() + level.slice(1);
            if (bypass) {
                badgeText = "Bypass ativo";
                this.scorePriority = "warning";
            } else if (historical) {
                badgeText = "Histórico";
            }

            this.scoreBadge = badgeText;
            this.scoreSummary = `Score ${amount} (${level})${bypass ? " - Bypass ativo" : ""}${historical ? " - Visualizando histórico" : ""}`;
        },

        updateBinSummary(data) {
            if (!data || !data.top_bins || data.top_bins.length === 0) {
                this.binSummary = "Nenhum BIN encontrado";
                this.binPriority = "normal";
                this.binBadge = null;
                return;
            }

            const topBins = data.top_bins;
            const summary = data.summary;

            // Determina prioridade baseada no resumo geral se disponível
            if (summary && summary.high_fraud_bins > 0) {
                this.binPriority = "danger";
                this.binBadge = `${summary.high_fraud_bins}/${summary.total_unique_bins} alto risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.high_fraud_bins} alto risco`;
            } else if (summary && summary.medium_fraud_bins > 0) {
                this.binPriority = "warning";
                this.binBadge = `${summary.medium_fraud_bins}/${summary.total_unique_bins} médio risco`;
                this.binSummary = `${summary.total_unique_bins} BINs, ${summary.medium_fraud_bins} médio risco`;
            } else {
                // Fallback para análise dos top bins se não houver resumo
                const highFraudBins = topBins.filter(
                    (bin) => bin.fraud_level === "high",
                ).length;
                const mediumFraudBins = topBins.filter(
                    (bin) => bin.fraud_level === "medium",
                ).length;
                const totalBins = summary?.total_unique_bins || topBins.length;

                if (highFraudBins > 0) {
                    this.binPriority = "danger";
                    this.binBadge = `${highFraudBins}/${totalBins} alto risco`;
                    this.binSummary = `${totalBins} BINs, ${highFraudBins} alto risco`;
                } else if (mediumFraudBins > 0) {
                    this.binPriority = "warning";
                    this.binBadge = `${mediumFraudBins}/${totalBins} médio risco`;
                    this.binSummary = `${totalBins} BINs, ${mediumFraudBins} médio risco`;
                } else {
                    this.binPriority = "normal";
                    this.binBadge = `${totalBins} BINs`;
                    this.binSummary = `${totalBins} BINs únicos`;
                }
            }
        },

        updatePaymentMethodSummary(data) {
            if (!data || data.length === 0) {
                this.paymentMethodSummary = "Nenhum método encontrado";
                this.paymentMethodPriority = "normal";
                this.paymentMethodBadge = null;
                return;
            }

            const highConcentration = data.filter(
                (method) => method.percentage > 70,
            ).length;
            const mediumConcentration = data.filter(
                (method) => method.percentage > 50,
            ).length;

            if (highConcentration > 0) {
                this.paymentMethodPriority = "warning";
                this.paymentMethodBadge = "Alta concentração";
                this.paymentMethodSummary = `${data.length} métodos, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.paymentMethodPriority = "warning";
                this.paymentMethodBadge = "Concentração média";
                this.paymentMethodSummary = `${data.length} métodos, concentração média`;
            } else {
                this.paymentMethodPriority = "normal";
                this.paymentMethodBadge = `${data.length} métodos`;
                this.paymentMethodSummary = `${data.length} métodos distribuídos`;
            }
        },

        updateSystemAnalysisSummary(analysis) {
            this.userAnalysis = analysis;

            if (!analysis) {
                this.systemAnalysisSummary = "Análise não disponível";
                this.systemAnalysisPriority = "normal";
                this.systemAnalysisBadge = null;
                return;
            }

            // Determina a prioridade baseada na confiança e análise
            if (analysis.analysis === "Reprovar" || analysis.confidence < 30) {
                this.systemAnalysisPriority = "danger";
            } else if (
                analysis.analysis === "Atenção" ||
                (analysis.confidence >= 30 && analysis.confidence < 70)
            ) {
                this.systemAnalysisPriority = "warning";
            } else {
                this.systemAnalysisPriority = "normal";
            }

            this.systemAnalysisBadge = `${analysis.confidence}%`;
            this.systemAnalysisSummary = `${analysis.analysis} (${analysis.confidence}% confiança)`;
        },

        updateCommissionTypesSummary(data) {
            if (!data || data.length === 0) {
                this.commissionTypesSummary =
                    "Nenhum tipo de comissão encontrado";
                this.commissionTypesPriority = "normal";
                this.commissionTypesBadge = null;
                return;
            }

            const highConcentration = data.filter(
                (commission) => commission.percentage > 70,
            ).length;
            const mediumConcentration = data.filter(
                (commission) => commission.percentage > 50,
            ).length;

            if (highConcentration > 0) {
                this.commissionTypesPriority = "warning";
                this.commissionTypesBadge = "Alta concentração";
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração alta`;
            } else if (mediumConcentration > 0) {
                this.commissionTypesPriority = "warning";
                this.commissionTypesBadge = "Concentração média";
                this.commissionTypesSummary = `${data.length} tipos de comissão, concentração média`;
            } else {
                this.commissionTypesPriority = "normal";
                this.commissionTypesBadge = `${data.length} tipos de comissão`;
                this.commissionTypesSummary = `${data.length} tipos de comissão distribuídos`;
            }
        },

        updateRunoffSummary(runoff) {
            // O card já emite o resumo pronto (ui_summary): a leitura de
            // status e a formatação vivem no próprio card, não aqui.
            if (!runoff?.ui_summary) {
                this.runoffSummary = "Runoff não disponível";
                this.runoffPriority = "normal";
                this.runoffBadge = null;
                this.runoffHeadline = "";
                return;
            }

            this.runoffPriority = runoff.ui_summary.priority;
            this.runoffBadge = runoff.ui_summary.badge;
            this.runoffSummary = runoff.ui_summary.summary;
            this.runoffHeadline = runoff.ui_summary.headline ?? "";

            // A decisão de segurar parcela não pode ficar atrás de um clique:
            // runoff descoberto/apertado abre a seção sozinho.
            if (runoff.ui_summary.priority !== "normal") {
                this.sections.runoff = true;
            }
        },

        generateDossier() {
            this.generatingDossier = true;

            const userId = this.$route.params.user_id ?? this.user.id;

            axios
                .post(`/api/user/${userId}/dossier/generate`)
                .then((response) => {
                    this.generatingDossier = false;

                    if (response.data.success && response.data.download_url) {
                        // Abrir URL em nova aba
                        window.open(response.data.download_url, "_blank");
                    } else {
                        throw new Error("Resposta inválida do servidor");
                    }
                })
                .catch(() => {
                    this.generatingDossier = false;
                });
        },
    },
};
</script>
